/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Box, Button, Divider, Flex, Heading, Select, TextInput, createForm } from '@applyboard/crystal-ui';
import { useNavigate } from 'react-router-dom';
import { AgentData, App, CreateAgentProps, ProfileState } from '../../clients';
import { validateEmailFormat } from '../../utils';
import { AppAccessSection, AppConfig, UserStateActions, UserWithState } from '../';
import { useGetAllAgencies } from '../../hooks/agencies/useGetAllAgencies';

export type AgentFormProps = {
  agentData?: AgentData;
  onSubmit?: (profileData: CreateAgentProps) => void;
  isLoading: boolean;
  isReadOnly?: boolean;
};

export type AmsAppAccessFieldValues = {
  [App.AMS]: boolean;
  amsRole?: string;
};

export type EnrolmentPlannerAppAccessFieldValues = {
  [App.EnrolmentPlanner]: boolean;
  enrolmentPlannerRole?: string;
};

export type AddAgentFieldValues = {
  email: string;
  givenName: string;
  familyName: string;
  agencyId: string;
} & AmsAppAccessFieldValues;

const { Form, Field } = createForm<AddAgentFieldValues>();

export function AgentForm({ agentData, isReadOnly, onSubmit, isLoading }: AgentFormProps): JSX.Element {
  const navigate = useNavigate();
  const { allAgenciesData, isAgenciesLoading } = useGetAllAgencies();

  const agentApps: AppConfig[] = [
    {
      appName: App.AMS,
      appLabel: 'Application Management',
      logoSrc: '/ams-logo.png',
      value: true, // Agents always have AMS access
    },
  ];

  const initialValues: AddAgentFieldValues = {
    email: agentData?.profile?.email ?? '',
    givenName: agentData?.profile?.givenName ?? '',
    familyName: agentData?.profile?.familyName ?? '',
    agencyId: agentData?.agency?.agencyId ?? '',
    [App.AMS]: true,
  };

  const canManageAgentState = !isReadOnly && agentData !== undefined;

  const userStateData: UserWithState | undefined = agentData
    ? {
        id: agentData.id,
        givenName: agentData.profile.givenName,
        familyName: agentData.profile.familyName,
        state: agentData.state as ProfileState,
      }
    : undefined;

  return (
    <Form
      onSubmit={(formValue) => {
        onSubmit &&
          onSubmit({
            profile: {
              email: formValue.email,
              givenName: formValue.givenName,
              familyName: formValue.familyName !== '' ? formValue.familyName : undefined,
            },
            agency: {
              agencyId: formValue.agencyId,
            },
          });
      }}
      defaultValues={initialValues}
    >
      <Flex py={{ xs: 2, sm: 8 }} justify="between" align="center" gap={10} grow={1}>
        <Flex direction="column" basis="100%">
          <Box pb={8}>
            <Heading level={2}>Agent information</Heading>
          </Box>
          <Box pb={8}>
            <Field
              // @ts-ignore
              as={TextInput}
              label="Email"
              name="email"
              required="Email is required."
              disabled={isReadOnly}
              validate={(email: string) => (validateEmailFormat(email) ? true : 'This email is invalid.')}
            />
          </Box>
          <Flex pb={8} gap={10}>
            <Flex.Item basis={{ xs: '100%', sm: '50%' }}>
              <Field
                // @ts-ignore
                as={TextInput}
                label="Given Name"
                name="givenName"
                required="Given name is required."
                disabled={isReadOnly}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: '50%' }}>
              <Field
                // @ts-ignore
                as={TextInput}
                label="Family Name"
                name="familyName"
                disabled={isReadOnly}
              />
            </Flex.Item>
          </Flex>
          <Flex pb={8} gap={10}>
            <Flex.Item basis={{ xs: '100%', sm: '50%' }}>
              <Field
                appearance="native"
                as={Select}
                name="agencyId"
                placeholder={isAgenciesLoading ? 'Loading agencies' : 'Select'}
                label="Agency name"
                required="Agency name is required"
                disabled={isAgenciesLoading || isReadOnly}
                children={allAgenciesData?.data.map((agency) => (
                  <Select.Option value={agency.id} label={agency.attributes.name} key={agency.id} />
                ))}
              />
            </Flex.Item>
          </Flex>
          <Box pb={6}>
            <Divider />
          </Box>
          <AppAccessSection isDisabled={true} Field={Field} apps={agentApps} noticeType="agent" showBorder={true} />
          {!isReadOnly && (
            <>
              <Box py={{ xs: 8, sm: 10 }}>
                <Divider />
              </Box>
              <Flex justify="end" gap={4}>
                <UserStateActions
                  userData={userStateData}
                  canManageUserState={canManageAgentState}
                  redirectPath="/profiles/agent-users"
                  userType="Agent"
                />
                <Button
                  type="button"
                  onClick={() => {
                    navigate('/profiles/agent-users');
                  }}
                  intent="primary"
                  emphasis="outlined"
                >
                  Cancel
                </Button>
                <Button loading={isLoading} type="submit" intent="primary">
                  {agentData ? 'Update Agent' : 'Add Agent'}
                </Button>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </Form>
  );
}
